import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Instagram, 
  Globe, 
  Mail, 
  Phone, 
  MapPin,
  ArrowUpRight 
} from "lucide-react";

const Footer = () => {
  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Contact", path: "/contact" },
  ];

  const services = [
    { name: "Application Development", path: "/services/application-development" },
    { name: "Web Development", path: "/services/web-development" },
    { name: "Cryptocurrency Development", path: "/services/cryptocurrency-development" },
    { name: "IT Consultancy", path: "/services/it-consultancy" },
  ];

  const socialLinks = [
    { icon: Instagram, href: "https://instagram.com/greatworldinfotech", label: "Instagram" },
    { icon: Globe, href: "https://www.greatworldinfotechpvtltd.com", label: "Website" },
  ];

  return (
    <footer className="relative bg-[#030712] border-t border-white/10" data-testid="footer">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-blue-600/5 to-transparent pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_gwit-landing/artifacts/c7ifyjsg_WhatsApp%20Image%202026-03-13%20at%202.03.49%20PM.jpeg"
                alt="GWIT Logo"
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              <strong>GWIT</strong> - Great World Infotech Pvt Ltd (GREATWORLDINFOTECHPRIVATELIMITED) is a leading tech-based IT solutions company transforming businesses worldwide with innovative software development, app development, web development, and blockchain solutions.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-400 hover:text-blue-400 hover:border-blue-500/30 transition-all"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="text-white font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center gap-1 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="text-white font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center gap-1 group"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {service.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="text-white font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <a 
                  href="tel:+916361535018" 
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  +91 6361-535018
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <a 
                  href="mailto:greatworldinfotechpvtltd@gmail.com" 
                  className="text-slate-400 hover:text-white transition-colors text-sm break-all"
                  data-testid="footer-email"
                >
                  greatworldinfotechpvtltd@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm" data-testid="footer-address">
                  Shop 2, Ground Floor, Doddatoguru,<br />
                  Electronic City, Bangalore - 560100
                </span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm" data-testid="copyright">
            &copy; {new Date().getFullYear()} Great World Infotech Pvt Ltd. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            Innovate. Connect. Future.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
